import {
  FC,
  RefObject,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FieldErrors, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isUndefined } from 'lodash'
import sum from 'lodash/sum'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { Tab, TabPanel, Tabs } from '@components/Tabs'
import {
  ExternalTextSettings,
  GetTextParamsHandle,
  TextSettings,
  TextSettingsStateType,
} from '@components/TextSettings'

import { getParamsString } from '@helpers'
import { GENERATOR_INPUT_TYPE, REGEX } from '@constants'
import { ObjectFieldDTO } from '@types'

import {
  BindField,
  ConfigField,
  DATE_TIME_TYPE,
  MAX_DURATION_TYPE,
  PageContext,
  SegmentFieldType,
  SegmentType,
} from '@gantt/components/GanttCreateOrEdit'
import { GanttSegmentLinksHelper } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components/AddSegmentDialog/GanttSegmentLinksHelper'
import { SegmentIconsFields } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components/SegmentIconsFields'
import { RESOURCE_KEY_FIELD_PREFIX, RESOURCE_LINK_FIELD_PREFIX } from '@gantt/constants'
import {
  dateTimeFieldOptionsFilter,
  durationFieldOptionsFilter,
  getBindTypeOptionsByEnum,
  keyFieldOptionsFilter,
} from '@gantt/helpers'

import { BaseFormFields, SegmentLabelsFields } from '../../components'

//todo actual?
type Props = {
  type: SegmentType
  objectFieldsForValidation?: ObjectFieldDTO[]
  errors?: FieldErrors<T>
  textSettingsRef?: RefObject<GetTextParamsHandle>
  initialTextState?: TextSettingsStateType
  onTextSettingsDirty?: (value: ExternalTextSettings) => void
}

export const SegmentItem: FC<Props> = props => {
  const { errors, textSettingsRef, initialTextState, onTextSettingsDirty } = props

  const { t } = useTranslation()
  const { watch, setValue, resetField, setError, clearErrors } = useFormContext()
  const [isShowScriptDialog, setIsShowScriptDialog] = useState(false)
  const [currentTab, setCurrentTab] = useState(0)
  const { watchedObjectResource, modalType, currentBarObject, currentBarIndex, existingPaths } =
    useContext(PageContext)

  const watchResourceKeyField = watch(`link.resourceKeyField`)
  const watchSystemName = watch(`link.systemName`)
  const watchResourceLinkField = watch(`link.resourceLinkField`)
  const watchLinkAxisX = watch(`link.axisX`)
  const watchLinkAxisY = watch(`link.axisY`)
  const watchDatetimeStart: BindField = watch('datetimeStart')
  const watchDatetimeEnd: BindField = watch('datetimeEnd')
  const watchDuration: BindField = watch('duration')
  const watchMaxDuration: BindField = watch('maxDuration')
  const watchSegmentHeight = watch('segmentHeight')

  const isNoBarObject = !currentBarObject

  useEffect(() => {
    if (watchResourceKeyField !== `${RESOURCE_KEY_FIELD_PREFIX}${watchSystemName}`) {
      !watchSystemName
        ? resetField(`link.resourceKeyField`)
        : setValue(`link.resourceKeyField`, `${RESOURCE_KEY_FIELD_PREFIX}${watchSystemName}`)
    }

    if (watchResourceLinkField !== `${RESOURCE_LINK_FIELD_PREFIX}${watchSystemName}`) {
      !watchSystemName
        ? resetField(`link.resourceLinkField`)
        : setValue(`link.resourceLinkField`, `${RESOURCE_LINK_FIELD_PREFIX}${watchSystemName}`)
    }
  }, [watchSystemName])

  const toggleOpenScriptValueDialog = useCallback(() => {
    setIsShowScriptDialog(prevState => !prevState)
  }, [])

  const handleErrorLinks = () => {
    setError(`link.axisX.field.pathStr`, { type: 'custom', message: '' })
    setError(`link.axisY.field.pathStr`, { type: 'custom', message: '' })
  }

  const handleCorrectLinks = () => {
    clearErrors(`link.axisX.field.pathStr`)
    clearErrors(`link.axisY.field.pathStr`)
  }

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const selectedDates: string[] = useMemo(() => {
    return (
      [
        getParamsString(watchDuration),
        getParamsString(watchDatetimeStart),
        getParamsString(watchDatetimeEnd),
        getParamsString(watchMaxDuration),
      ] as (string | undefined)[]
    ).filter(item => !isUndefined(item))
  }, [watchDatetimeEnd, watchDatetimeStart, watchDuration, watchMaxDuration])

  const fieldPicker = useMemo(() => {
    return {
      existingPaths,
    }
  }, [existingPaths])

  const fieldPickerDate = useMemo(() => {
    return {
      existingPaths,
      selectedPaths: selectedDates,
    }
  }, [existingPaths, selectedDates])

  const handleClearDateFieldsError = useCallback(
    (value: ConfigField) => {
      const filledDatesCount = sum([
        Boolean(getParamsString(watchDatetimeStart)),
        Boolean(getParamsString(watchDatetimeEnd)),
        watchDuration && Boolean(getParamsString(watchDuration)),
      ])

      if (filledDatesCount >= 2) {
        clearErrors('datetimeStart')
        clearErrors('datetimeEnd')
        clearErrors('duration')
      }
    },
    [watchDatetimeStart, watchDatetimeEnd, watchDuration]
  )

  const hiddenSegmentFields = useMemo(() => {
    return new Map([
      ['boxVerticalGuideLines', true],
      ['parameters', true],
      ['borders.inner', true],
      ['borders.outer', true],
      ['lines.top.color', true],
      ['lines.top.width', true],
      ['lines.bottom.color', true],
      ['lines.bottom.width', true],
      ['textColor', true],
    ])
  }, [])

  return (
    <Box p={2}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label={t('ganttCreate.timelineForm.segmentSteps.data')} />
          <Tab label={t('ganttCreate.timelineForm.segmentSteps.appearance')} />
          <Tab label={t('ganttCreate.timelineForm.segmentSteps.edgeLabels')} />
          <Tab label={t('ganttCreate.timelineForm.segmentSteps.edgeIcons')} />
        </Tabs>
      </Box>
      <TabPanel index={0} sx={{ p: 1 }} value={currentTab}>
        <FormInput
          disabled={modalType === 'edit'}
          inputType={GENERATOR_INPUT_TYPE.INPUT}
          label={t('ganttCreate.timelineForm.systemName')}
          name={`link.systemName`}
          placeholder={t('ganttCreate.timelineForm.systemName')}
          replacePattern={REGEX.MODEL_CODE_REPLACE_PATTERN}
          rules={{
            required: true,
            validate: value => value.trim().length !== 0,
          }}
        />
        <Box component='fieldset' mb={1}>
          <legend>{t('ganttCreate.objectLinks')}</legend>
          <GanttSegmentLinksHelper
            linkAxisX={watchLinkAxisX}
            linkAxisY={watchLinkAxisY}
            onCorrectLinks={handleCorrectLinks}
            onErrorLinks={handleErrorLinks}
          />
          <BindTypeInputs
            bindTypeOptions={[]}
            containerName='link.axisY'
            fieldPicker={fieldPicker}
            isDisabled={!watchedObjectResource}
            object={watchedObjectResource}
            optionsFilter={keyFieldOptionsFilter}
            blockLabel={t('ganttCreate.timelineForm.segmentAxisYKey', {
              label: watchedObjectResource?.label,
              id: watchedObjectResource?.id,
            })}
          />
          <div style={{ width: 50 }} />
          <BindTypeInputs
            bindTypeOptions={[]}
            containerName='link.axisX'
            fieldPicker={fieldPicker}
            isDisabled={isNoBarObject}
            object={currentBarObject}
            optionsFilter={keyFieldOptionsFilter}
            blockLabel={t('ganttCreate.timelineForm.segmentAxisXKey', {
              label: currentBarObject?.label,
              id: currentBarObject?.id,
            })}
          />
          <div hidden>
            <FormInput
              disabled
              readOnly
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={t('ganttCreate.resourceField')}
              name={`link.resourceKeyField`}
            />
          </div>
        </Box>
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(DATE_TIME_TYPE)}
          containerName='datetimeStart'
          fieldPicker={fieldPickerDate}
          fieldsetTitle={t('ganttCreate.timelineForm.dateTimeStart')}
          isDisabled={isNoBarObject}
          object={currentBarObject}
          optionsFilter={dateTimeFieldOptionsFilter}
          prefix={`bar_${currentBarIndex}_segment_${watchSystemName}_${SegmentFieldType.DATETIME_START}`}
          onSaveField={handleClearDateFieldsError}
        />
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(DATE_TIME_TYPE)}
          containerName='datetimeEnd'
          fieldPicker={fieldPickerDate}
          fieldsetTitle={t('ganttCreate.timelineForm.dateTimeEnd')}
          isDisabled={isNoBarObject}
          object={currentBarObject}
          optionsFilter={dateTimeFieldOptionsFilter}
          prefix={`bar_${currentBarIndex}_segment_${watchSystemName}_${SegmentFieldType.DATETIME_END}`}
          onSaveField={handleClearDateFieldsError}
        />
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(DATE_TIME_TYPE)}
          containerName='duration'
          fieldPicker={fieldPickerDate}
          fieldsetTitle={t('ganttCreate.timelineForm.duration')}
          isDisabled={isNoBarObject}
          object={currentBarObject}
          optionsFilter={durationFieldOptionsFilter}
          onSaveField={handleClearDateFieldsError}
        />
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(MAX_DURATION_TYPE)}
          containerName='maxDuration'
          fieldPicker={fieldPickerDate}
          fieldsetTitle={t('ganttCreate.timelineForm.maxDuration')}
          isDisabled={isNoBarObject}
          object={currentBarObject}
          optionsFilter={durationFieldOptionsFilter}
          onSaveField={handleClearDateFieldsError}
        />
      </TabPanel>
      <TabPanel index={1} sx={{ p: 1 }} value={currentTab}>
        <BaseFormFields
          currentBarKey={`bar_${currentBarIndex}`}
          disabled={isNoBarObject}
          errors={errors}
          existingPaths={existingPaths}
          hiddenFields={hiddenSegmentFields}
          isShowScriptDialog={isShowScriptDialog}
          keyValue={`segment_${watchSystemName}`}
          toggleOpenScriptValueDialog={toggleOpenScriptValueDialog}
          watchedObject={currentBarObject}
          afterTitlesSlot={
            <Box component='fieldset'>
              <legend>{t('ganttCreate.timelineForm.text')}</legend>
              <FormInput
                inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                label={t('ganttCreate.timelineForm.useTextSettings')}
                labelPlacement='end'
                labelSx={{ width: 'auto', minWidth: 'max-content' }}
                name='textSettings.useTextSettings'
              />
              <TextSettings
                ref={textSettingsRef}
                backgroundSettings={false}
                borderSettings={false}
                initialTextState={initialTextState}
                isEdit={!isNoBarObject && Boolean(watch('textSettings.useTextSettings'))}
                labelPlacement='top'
                labelText=''
                onSetExternalDirty={onTextSettingsDirty}
              />
            </Box>
          }
        />
      </TabPanel>
      <TabPanel index={2} sx={{ p: 1 }} value={currentTab}>
        <SegmentLabelsFields existingPaths={existingPaths} watchedObject={currentBarObject} />
      </TabPanel>
      <TabPanel index={3} sx={{ p: 1 }} value={currentTab}>
        <SegmentIconsFields existingPaths={existingPaths} watchedObject={currentBarObject} />
      </TabPanel>
    </Box>
  )
}
